/**
 * @fileoverview The location selector for a social post
 *
 * @author Samantha Landen (slanden@yext.com)
 */
goog.module('yext.social.post.LocationSelector');
goog.module.declareLegacyNamespace();

const yext = goog.require('yext');
const browser = goog.require('yext.browser');
const Model = goog.require('yext.mvc.Model');
/** @suppress {extraRequire} */
goog.require('yext.plugins.inputchange');
/** @suppress {extraRequire} */
goog.require('yext.plugins.socialLocationPicker');
const partners = goog.require('yext.social.partners');
/** @suppress {extraRequire} */
goog.require('yext.storm.FolderTree');
const routes = goog.require('yext.storm.routes');


/**
 * @constructor
 * @param {!jQuery} The post form element
 * @param {Function} updateFn The callback function to update the post when a
 *                            new location selection is made
 */
const LocationSelector = function($ctxt, updateFn) {
  this.$ctxt = $ctxt;
  this.$associationDialog = $('#location-association-dialog');
  this.$postLocation = this.$ctxt.find('.js-location-post-details');
  this.$locationSelect = this.$associationDialog.find('.js-location-association-select');
  this.$locationChosen = this.$associationDialog.find('.js-location-post-select');
  this.$folderSelect = this.$associationDialog.find('.js-folder-container');
  this.updateFn = updateFn;

  /**
   * @type {Model}
   * @private
   */
  this._model = new Model();

  /**
   * @private
   */
  this._originalState = {};

  this.init();
};

/**
 * @enum {number}
 */
LocationSelector.ScopeType = {
  'ALL': 1,
  'FOLDER': 2,
  'LOCATION': 3,
  'CUSTOM': 4,
};

LocationSelector.REGEX = {
  paramIds: /&?ids=([^&]$|[^&]*)/i,
  paramFrom: /&?from=([^&]$|[^&]*)/,
  paramIncludes: /&?includes=([^&]$|[^&]*)/i,
};

/**
 * Initializes the location selector interactions
 */
LocationSelector.prototype.init = function() {
  this.initModel();
  this.bindModelToRender();
  this.initDialog();
  this.bindCancelDialog();
  this.bindSubmitDialog();
  this.bindDialogOpen();
  this.bindChangeLocationType();
  this.bindLocationSelect();
  this.bindSubmitToggle();
};

/**
 * Initializes the model
 */
LocationSelector.prototype.initModel = function() {
  var selected = this.$associationDialog.find('.js-location-type:checked').val();
  var ajax = this.$associationDialog.find('.js-submit').data('ajax');
  var locationId = this.$locationSelect.val() || null;
  this._model.init({
    /**
     * @type {LocationSelector.ScopeType}
     */
    'selected': LocationSelector.ScopeType[selected],
    'locationId': locationId,
    'folderId': null,
    'ajax': ajax,
  });
  this._originalState = this._model.raw();
};

/**
 * Re-renders the view when the model changes
 */
LocationSelector.prototype.bindModelToRender = function() {
  var self = this;
  this._model.on('change, reset', function() {
    self.render();
  });
};

/**
 * Initializes the location selection dialog
 */
LocationSelector.prototype.initDialog = function() {
  var self = this;
  this.$associationDialog.dialog({
    title: yext.msg('Select locations to post to'),
    modal: true,
    autoOpen: false,
    draggable: false,
    resizable: false,
    width: 600,
    close: function() {
      self._model.resetState(self._originalState);
    },
  });
};

/**
 * Updates the location scope for the post based on the given selection
 *
 * @param {string} selected The type of the selection (e.g., LOCATIONS, FOLDER, etc)
 */
LocationSelector.prototype.updateSelected = function() {
  switch (this._model.get('selected')) {
    case LocationSelector.ScopeType['ALL']:
      this.selectAllLocations();
      break;
    case LocationSelector.ScopeType['FOLDER']:
      this.selectFolder();
      break;
    case LocationSelector.ScopeType['LOCATION']:
      this.selectLocation();
      break;
  }
};

/**
 * Updates the location scope to "All Locations"
 */
LocationSelector.prototype.selectAllLocations = function() {
  this.updateFn(null /* data */, this.updateHelper.bind(this));
};

/**
 * Updates the location scope to a folder
 */
LocationSelector.prototype.selectFolder = function() {
  var self = this;

  this.doAjax(routes.SocialPost.getPartnersForFolder({
    'folderId': self._model.get('folderId'),
  }))
    .done(function(data) {
      self.updateFn(data, self.updateHelper.bind(self));
    });
};

/**
 * Updates the location scope to a single location
 *
 * @param {boolean} ajax True if locations must be loaded via ajax
 */
LocationSelector.prototype.selectLocation = function(ajax) {
  var self = this;
  var locationId = this._model.get('locationId');

  if (!this._model.get('ajax') && partners.locationsToPartnerIds) {
    this.updateFn({
      'partners': partners.locationsToPartnerIds[locationId],
      'hasSocialWidgets': partners.locationsToWidgets[locationId],
    }, this.updateHelper.bind(this));
  } else {
    this.doAjax(routes.SocialPost.getPartnersForLocation({
      'locationId': locationId,
    }))
      .done(function(data) {
        self.updateFn(data, self.updateHelper.bind(self));
      });
  }
};

/**
 * If an update was successful, closes the dialog and updates the UI.
 * Otherwise, resets the state of the model
 *
 * @param {boolean} success True if update was successful
 */
LocationSelector.prototype.updateHelper = function(success) {
  if (success) {
    this._originalState = this._model.raw();
    this.closeDialog();
  } else {
    this._model.resetState(this._originalState);
  }
};

/**
 * Does an ajax request to the given url and updates the location scope accordingly
 *
 * @param {string} url Url for the request
 * @return The request promise
 */
LocationSelector.prototype.doAjax = function(url) {
  if (!yext.isString(url)) {
    throw new TypeError('LocationSelector.doAjax: Invalid url provided, expected string');
  }
  var self = this;
  return $.ajax({
    dataType: 'json',
    type: 'GET',
    url: url,
  });
};

/**
 * Binds clicking the cancel button
 */
LocationSelector.prototype.bindCancelDialog = function() {
  var self = this;
  this.$associationDialog.find('.js-cancel').on('click', function(e) {
    e.stopPropagation();
    self.closeDialog();
    return false;
  });
};

/**
 * Binds clicking the submit button
 */
LocationSelector.prototype.bindSubmitDialog = function() {
  var self = this;
  this.$associationDialog.find('.js-submit').on('click', function(e) {
    e.stopPropagation();
    if ($(this).hasClass('disabled')) {
      return false;
    }
    self.updateSelected();
  });
};

/**
 * Bind the location type (all, folder, location) radio buttons
 */
LocationSelector.prototype.bindChangeLocationType = function() {
  var self = this;

  this.$associationDialog.find('.js-location-type').inputchange(function() {
    var selected = LocationSelector.ScopeType[$(this).val()];
    self._model.set({
      'selected': selected,
      'folderId': null,
      'locationId': null,
    });
  });
};

/**
 * Binds opening the dialog when the "select locations on" link is clicked
 */
LocationSelector.prototype.bindDialogOpen = function() {
  var self = this;
  this.$ctxt.find('.js-location-post-details').on('click', function(e) {
    e.stopPropagation();
    self.$associationDialog.on('dialogopen', function() {
      // disabling auto focus on dialog
      self.$associationDialog.find('input').blur();
    });
    self.$associationDialog.dialog('open');

    var zIndex = self.$associationDialog.parent().css('z-index');
    self.$associationDialog.find('.chosen-drop').css('z-index', zIndex + 1);
    self.checkValidInput();
    return;
  });
};

/**
 * Binds the location select dropdown
 */
LocationSelector.prototype.bindLocationSelect = function() {
  var self = this;
  $(this.$locationSelect).socialLocationPicker();
  this.$associationDialog.find('.chosen-container, .chosen-drop').css('width', '400px');
  this.$locationSelect.inputchange(function() {
    self._model.set({
      'locationId': self.$locationSelect.val(),
    });
  });
};

/**
 * Bind enabling/disabling the submit button when the dialog state changes
 */
LocationSelector.prototype.bindSubmitToggle = function() {
  var self = this;
  this.$associationDialog
    .find('.js-location-association-form input, .js-location-association-select')
    .inputchange(function() {
      self.checkValidInput();
    });
};

/**
 * Closes the location selection dialog
 */
LocationSelector.prototype.closeDialog = function() {
  this.$associationDialog.dialog('close');
};

/**
 * Removes custom location filter and any related url params
 * only runs if the custom location is found on the UI
 */
LocationSelector.prototype.removeCustomLocationFilter = function() {
  var $customFilter = this.$associationDialog.find('.js-custom-location-filter');

  if ($customFilter.length) {
    $customFilter.remove();
    // > IE9 supports history.pushState otherwise we'll just leave the URL as is
    // the other option is to use location.href but that will refresh the browser
    // and is an undesired UX
    if (browser.hasHistory()) {
      // remove the url params from the custom location filter
      var queryString = window.location.href
        .replace(LocationSelector.REGEX.paramIds, '')
        .replace(LocationSelector.REGEX.paramFrom, '')
        .replace(LocationSelector.REGEX.paramIncludes, '');
      window.history.pushState({}, '', queryString);
    }
  }
};

/**
 * Checks if the location selection is valid and enables/disables the dialog
 * save button accordingly
 */
LocationSelector.prototype.checkValidInput = function() {
  var validInput = true;

  if (this._model.get('selected') == LocationSelector.ScopeType['FOLDER']
      && !this._model.get('folderId')) {
    validInput = false;
  }

  if (this._model.get('selected') == LocationSelector.ScopeType['LOCATION']
      && !this._model.get('locationId')) {
    validInput = false;
  }

  // If we aren't using the selector yet, any input is valid.
  if (!this.$ctxt.find('.js-uses-selector').val()) {
    validInput = true;
  }

  this.$associationDialog.find('.js-submit').toggleClass('disabled', !validInput);
};

/**
 * Initializes the folder tree
 */
LocationSelector.prototype.setupTree = function() {
  var idPrefix = 'fb-post-tree-id-';
  var self = this;

  this.$folderSelect.simpleFolderTree({
    'topLevelFolders': window.topLevelFolders,
    'idPrefix': idPrefix,
    'onActivate': function(node) {
      self._model.set({
        'folderId': node.data.key,
      });
      self.$associationDialog.find('.js-folder-id').val(node.data.key).trigger('change');
    },
  });
  this.$folderSelect.find('#' + idPrefix + '0 .dynatree-node').hide();
};

/**
 * @return the URI-encoded location search param based on the selected location scope
 */
LocationSelector.prototype.encodeLocationSearchParam = function() {
  var selected = this._model.get('selected');
  if (selected == LocationSelector.ScopeType['CUSTOM']) {
    return encodeURIComponent(window.location.search.substring(1));
  } else if (selected == LocationSelector.ScopeType['LOCATION']) {
    return encodeURIComponent('ids=' + this._model.get('locationId'));
  } else if (selected == LocationSelector.ScopeType['FOLDER']) {
    return encodeURIComponent('folderId=' + this._model.get('folderId'));
  } else if (selected == LocationSelector.ScopeType['ALL']) {
    return encodeURIComponent('allLocationsPost=true');
  }
};

/**
 * @return a text description of the current location scope
 */
LocationSelector.prototype.getDescriptiveText = function() {
  switch (this._model.get('selected')) {
    case LocationSelector.ScopeType['ALL']:
    // TRANSLATORS: Make a social post to all available locations on social network for
    // the business. There is no text after 'on' as social network logo is used instead.
    // Example: Post to all locations on (Facebook icon) (Google+ icon) (Foursquare icon) (Website icon)
      return yext.msg('Post to all locations on ');
    case LocationSelector.ScopeType['FOLDER']:
    // TRANSLATORS: Make a social post to all the locations that are in the selected
    // folder. There is no text after 'on' as social network logo is used instead.
    // Example: Post to all locations in selected folder on (Facebook icon) (Google+ icon) (Foursquare icon) (Website icon)
      return yext.msg('Post to all locations in selected folder on ');
    case LocationSelector.ScopeType['LOCATION']:
      var location = this.$associationDialog.find('.js-location-option:selected').text();
      // TRANSLATORS: Make a post to a specific location on social network.
      // There is no text after 'on' as social network logo is used instead.
      // EXAMPLE: Post to 1 Madison ave, NY on (Facebook icon) (Google+ icon) (Foursquare icon) (Website icon)
      return yext.msg('Post to {0} on ', location);
    default:
    // TRANSLATORS: Make a social post to select locations on social network for the business.
    // There is no text after 'on' as social network logo is used instead.
    // Example: Post to all locations on (Facebook icon) (Google+ icon) (Foursquare icon) (Website icon)
      return yext.msg('Post to select locations on ');
  }
};

LocationSelector.prototype.getFolderId = function() {
  return this._model.get('folderId');
};

LocationSelector.prototype.getLocationId = function() {
  return this._model.get('locationId');
};

/**
 * Updates the UI based on the saved state of the location selector
 */
LocationSelector.prototype.render = function() {
  var locationId = this._model.get('locationId');
  var folderId = this._model.get('folderId');
  var selected = this._model.get('selected');

  if (selected != LocationSelector.ScopeType['CUSTOM']) {
    this.removeCustomLocationFilter();
  }

  var selectedName = yext.enumToString(LocationSelector.ScopeType, selected);
  this.$associationDialog.find('.js-location-type[value="' + selectedName + '"]').prop('checked', true);
  if (selected == LocationSelector.ScopeType['FOLDER']
      && !this.$associationDialog.find('.dynatree-container').length) {
    this.setupTree();
    this.$locationChosen.hide();
  } else if (selected == LocationSelector.ScopeType['FOLDER']) {
    this.$folderSelect.show();
    this.$locationChosen.hide();
  } else if (selected == LocationSelector.ScopeType['LOCATION']) {
    this.$folderSelect.hide();
    this.$locationChosen.show();
  } else {
    this.$locationChosen.hide();
    this.$folderSelect.hide();
  }

  if (!locationId) {
    this.$locationChosen.find('.search-choice-close').trigger('mouseup');
  } else {
    var index = this.$locationSelect.find('.js-location-option[value="' + locationId + '"]').index();
    this.$locationChosen.find('[data-option-array-index="' + index + '"]').trigger('mouseup');
  }
  if (this.$associationDialog.find('.dynatree-container').length) {
    this.$folderSelect.dynatree('getTree').activateKey(folderId);
  }
};

exports = LocationSelector;
